import React, { ReactNode, useState } from 'react'
import TextField from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import { useTheme } from '@mui/material/styles'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'

export interface InputFieldWithIconProps {
  icon: ReactNode
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void
  placeholder?: string
  isPasswordField?: boolean
  autocomplete?: string
  id?: string
  fieldBackgroundColor?: string
  fieldBorderColor?: string
}

const InputFieldWithIcon = (props: InputFieldWithIconProps) => {
  const theme = useTheme()
  const [showPassword, setShowPassword] = useState(false)
  const inputBackgroundColor =
    props.fieldBackgroundColor ??
    theme.palette.card?.primary ??
    'var(--color-surface-card)'
  const inputBorderColor =
    props.fieldBorderColor ??
    theme.palette.border?.primary ??
    'var(--color-border-primary)'

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <TextField
      id={props.id}
      variant="outlined"
      placeholder={props.placeholder}
      type={props.isPasswordField && !showPassword ? 'password' : 'text'}
      autoComplete={'off'}
      autoCorrect="off"
      sx={{
        width: '100%',
        borderColor: inputBorderColor,
        height: '48px',
        '& .MuiOutlinedInput-root': {
          backgroundColor: inputBackgroundColor,
          color: theme.palette.text?.primary,
          '& fieldset': {
            borderColor: inputBorderColor
          },
          '&:hover fieldset': {
            borderColor: inputBorderColor
          },
          '&.Mui-focused fieldset': {
            borderColor: inputBorderColor
          }
        },
        '& fieldset': {
          borderWidth: '1px',
          borderRadius: '24px'
        },
        '& input': {
          height: '30px',
          padding: theme.spacing(0, 2),
          color: theme.palette.text?.primary,
          // Targeting the input field for autofill styles
          '&:-webkit-autofill': {
            WebkitBoxShadow: `0 0 0 1000px ${inputBackgroundColor} inset`,
            WebkitTextFillColor: theme.palette.text?.primary
          },
          '&:-webkit-autofill:hover': {
            WebkitBoxShadow: `0 0 0 1000px ${inputBackgroundColor} inset`,
            WebkitTextFillColor: theme.palette.text?.primary
          },
          '&:-webkit-autofill:focus': {
            WebkitBoxShadow: `0 0 0 1000px ${inputBackgroundColor} inset`,
            WebkitTextFillColor: theme.palette.text?.primary
          },
          '&:-webkit-autofill:active': {
            WebkitBoxShadow: `0 0 0 1000px ${inputBackgroundColor} inset`,
            WebkitTextFillColor: theme.palette.text?.primary
          }
        },
        '& input::placeholder': {
          color: theme.palette.input?.placeholder,
          opacity: 1
        }
      }}
      onChange={props.onChange}
      InputProps={{
        sx: {
          height: '48px'
        },

        startAdornment: (
          <InputAdornment position="start">
            <IconButton
              tabIndex={-1}
              sx={{
                color: theme.palette.input?.placeholder,
                '& svg, & svg path, & svg rect, & svg circle, & svg ellipse': {
                  color: 'inherit',
                  fill: 'currentColor',
                  stroke: 'currentColor'
                }
              }}
            >
              {props.icon}
            </IconButton>
          </InputAdornment>
        ),
        endAdornment: props.isPasswordField ? (
          <InputAdornment position="end">
            <IconButton
              onClick={togglePasswordVisibility}
              sx={{ color: theme.palette.input?.placeholder }}
            >
              {showPassword ? <VisibilityOff /> : <Visibility />}
            </IconButton>
          </InputAdornment>
        ) : null
      }}
    />
  )
}

export default InputFieldWithIcon
